import { useState } from 'react';

const faqs = [
  {
    question: 'What wind energy solutions are available in Australia?',
    answer: "Australia offers a full range of wind energy solutions including large-scale utility wind farms (10 MW to 1,000 MW+), commercial and industrial Corporate Power Purchase Agreements (CPPAs), small wind turbines for farms and remote properties (1 kW to 300 kW), hybrid wind-solar-battery microgrids for off-grid communities and mining operations, and offshore wind projects under development. AK Energy connects businesses and landowners with providers across all these categories.",
  },
  {
    question: 'Is wind energy suitable for businesses in Australia?',
    answer: "Yes — Australian businesses can access wind energy through Corporate Power Purchase Agreements (CPPAs) without any on-site infrastructure. A CPPA locks in a fixed electricity price for 10–25 years, dramatically reducing long-term energy cost risk. Businesses also receive Large-scale Generation Certificates (LGCs) which count toward scope 2 emissions reduction and sustainability reporting. Industries from manufacturing and mining to retail and data centres are actively pursuing wind energy contracts.",
  },
  {
    question: 'How do I find trusted wind energy providers in Australia?',
    answer: "AK Energy is Australia's trusted renewable energy marketplace connecting customers with verified wind energy developers, project managers, and small wind turbine installers. All providers are vetted for relevant accreditations, project experience, and financial standing. You can compare providers by project type, location, specialisation, and client reviews through the AK Energy marketplace.",
  },
  {
    question: 'How much does a commercial wind farm cost in Australia?',
    answer: "Large-scale commercial wind farms in Australia cost approximately $1,800–$2,500 per kW installed — meaning a 100 MW wind farm costs $180–$250 million. For businesses accessing wind energy through a Corporate PPA, there is no capital cost — you simply sign a contract to purchase electricity at a fixed price per MWh. For small wind turbines on farms or remote properties, costs range from $20,000 for a 5 kW system to $1.5 million for a 300 kW installation.",
  },
  {
    question: 'What areas of Australia are best for wind energy?',
    answer: "Australia's best wind resources are found in: South Australia (particularly Eyre Peninsula, Yorke Peninsula, and the South-East), Western Victoria and the Grampians region, the South West of Western Australia, Tasmania (the windiest state), Bass Strait coastal areas, and parts of NSW and QLD. AK Energy's network covers all of these regions.",
  },
  {
    question: 'Can wind energy be combined with solar and battery storage?',
    answer: "Yes — wind-solar-battery hybrid systems are increasingly common in Australia, particularly for remote and off-grid applications. Wind energy tends to peak during winter and at night, while solar peaks in summer and during daylight hours. This seasonal and daily complementarity means hybrid systems can achieve higher overall reliability and lower storage requirements than either technology alone. AK Energy connects clients with providers specialising in hybrid microgrid design and deployment.",
  },
  {
    question: 'What government incentives are available for wind energy in Australia?',
    answer: "Australia's renewable energy framework supports wind through several mechanisms: the Renewable Energy Target (RET) generates Large-scale Generation Certificates (LGCs) for every MWh produced; ARENA (Australian Renewable Energy Agency) provides grants and concessional funding for innovative projects; the CEFC (Clean Energy Finance Corporation) offers low-cost debt finance; state governments in SA, VIC, and QLD offer additional tender mechanisms and reverse auctions for new generation capacity; and remote communities can access specific funding programs for hybrid wind-solar-battery systems.",
  },
  {
    question: 'How long does a wind energy project take to develop in Australia?',
    answer: "The development timeline for a commercial wind farm in Australia is typically 4–8 years from initial site selection to first power, including resource assessment (1–2 years), planning and approvals (2–3 years), construction (1–2 years), and grid connection commissioning (3–6 months). Simpler projects in pre-approved zones can be faster. Small wind installations on farms or remote properties can be completed in 3–6 months.",
  },
];

export default function WindFAQ() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  const toggle = (idx: number) => {
    setOpenIdx(openIdx === idx ? null : idx);
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  };

  return (
    <section className="bg-background-50 py-20 md:py-28">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-12 lg:gap-16 items-start">
          {/* Left column */}
          <div className="lg:sticky lg:top-28">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-50 border border-secondary-200/50 mb-5">
              <i className="ri-question-answer-line text-secondary-600 text-sm"></i>
              <span className="text-xs font-semibold text-secondary-700 tracking-wide uppercase whitespace-nowrap">FAQ</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4">
              Wind Energy<br />Questions<br />Answered
            </h2>
            <p className="text-base text-foreground-600 leading-relaxed mb-8">
              From project costs and timelines to corporate PPAs and remote area microgrids — everything you need to know about wind energy in Australia.
            </p>
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-secondary-50 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-windy-line text-secondary-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground-800 mb-0.5">Related resource</p>
                  <a href="/solar-energy-australia" className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors">
                    Solar Energy Solutions Australia →
                  </a>
                </div>
              </div>
            </div>
            <a
              href="/#contact"
              className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-xl border-2 border-foreground-200 text-foreground-800 hover:border-foreground-400 hover:bg-foreground-50 transition-all duration-200 cursor-pointer"
            >
              Ask a Question
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          {/* FAQ list */}
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                  openIdx === idx
                    ? 'bg-secondary-50 border-secondary-200'
                    : 'bg-background-100/70 border-background-200/70 hover:border-background-300'
                }`}
              >
                <button
                  onClick={() => toggle(idx)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                >
                  <span className={`text-sm font-semibold leading-snug ${openIdx === idx ? 'text-secondary-700' : 'text-foreground-900'}`}>
                    {faq.question}
                  </span>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200 ${
                    openIdx === idx ? 'bg-secondary-500 text-white' : 'bg-background-200 text-foreground-600'
                  }`}>
                    <i className={`text-sm transition-transform duration-200 ${openIdx === idx ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </div>
                </button>
                {openIdx === idx && (
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}